import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import ArticleBody from "@/pages/article/components/ArticleBody";
import type { Article } from "@/types/content";

const formatDate = (value: string) => value.replace(/-/g, "年").replace(/年(\d+)$/, "月$1日");

const ArticleDetail = () => {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id) as Article | undefined;
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!article) return [] as Article[];
    const sameCat = articles.filter((item) => item.id !== article.id && item.category === article.category);
    const others = articles.filter((item) => item.id !== article.id && item.category !== article.category);
    return [...sameCat, ...others].slice(0, 3) as Article[];
  }, [article]);

  const headings = useMemo(
    () =>
      (article?.content || [])
        .map((block, index) => ({ block, index }))
        .filter((entry) => entry.block.type === "h2"),
    [article],
  );

  useSeo({
    title: article
      ? `${article.title} - 热门黑料历史回顾｜考古坞`
      : "条目不存在 - 考古坞",
    description: article
      ? `${article.excerpt} 考古坞按年代整理的热门黑料历史回顾与旧料翻出内容，附出处与年份考证。`
      : "这一条旧料暂时没有找到，返回考古合集继续浏览。",
    keywords: article
      ? `${article.tags.join(",")},热门黑料历史回顾,热门黑料旧料翻出`
      : "热门黑料,考古合集",
    image: article?.cover,
    type: "article",
    publishedTime: article?.date,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-shell px-4 py-24 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-search-line text-3xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">
            这一条还没归档
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            链接可能已经更正过，或者这条旧料还在整理中。先去合集里看看别的。
          </p>
          <Link
            to="/archive"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-archive-line text-base"></i>
            返回考古合集
          </Link>
        </div>
      </SiteShell>
    );
  }

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: "考古坞" },
      publisher: { "@type": "Organization", name: "考古坞" },
      keywords: article.tags.join(","),
      mainEntityOfPage: `https://kaogudock.cn/article/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://kaogudock.cn/" },
        { "@type": "ListItem", position: 2, name: "考古合集", item: "https://kaogudock.cn/archive" },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `https://kaogudock.cn/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <SiteShell>
      <JsonLd data={structuredData} id="article-jsonld" />

      <section className="border-b border-foreground-950/10 bg-background-100 paper-grain">
        <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "考古合集", to: "/archive" },
              { label: article.categoryName, to: `/archive?cat=${article.category}` },
              { label: article.title },
            ]}
          />
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2 font-label text-xs">
                <span className="rounded-sm bg-primary-500 px-2.5 py-1 text-background-50">
                  {article.categoryName}
                </span>
                <span className="rounded-sm bg-background-200 px-2.5 py-1 text-foreground-700">
                  {article.year} 年档
                </span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {article.excerpt}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line text-base"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-base"></i>
                  {formatDate(article.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-base"></i>
                  约 {article.readTime} 分钟
                </span>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="flex h-40 w-full overflow-hidden rounded-lg lg:h-48">
                <img
                  src={article.cover}
                  alt={`${article.title} · 旧料翻出档案配图`}
                  title={`${article.title} · 考古坞`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <ArticleBody blocks={article.content} />

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-foreground-950/10 pt-6">
                <span className="font-label text-xs text-foreground-500">标签</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/archive?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3 rounded-lg border border-foreground-950/10 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-label text-sm text-foreground-700">
                  觉得这条旧料有价值？把它转给同样在考古的人。
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-foreground-800"
                >
                  <i className={`${copied ? "ri-check-line" : "ri-link"} text-base`}></i>
                  {copied ? "链接已复制" : "复制本条链接"}
                </button>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                {headings.length > 0 && (
                  <nav
                    aria-label="本页目录"
                    className="rounded-lg border border-foreground-950/10 bg-background-100 p-5"
                  >
                    <h2 className="font-heading text-base font-bold text-foreground-950">本页目录</h2>
                    <ol className="mt-3 space-y-2">
                      {headings.map((entry) => (
                        <li key={entry.index}>
                          <a
                            href={`#section-${entry.index}`}
                            className="flex gap-2 font-label text-sm text-foreground-600 transition-colors hover:text-primary-600"
                          >
                            <i className="ri-corner-down-right-line mt-0.5 text-base text-foreground-400"></i>
                            {entry.block.text}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </nav>
                )}

                <div className="rounded-lg border border-foreground-950/10 bg-primary-50 p-5">
                  <p className="font-label text-xs tracking-[0.22em] text-primary-700">线索投递</p>
                  <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">
                    你手里有这一年的旧料吗？
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    如果有更完整的录像、报纸或当事人采访，欢迎交给档案室，我们会核实后补充进这一条。
                  </p>
                  <Link
                    to="/#contact"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line text-base"></i>
                    写下你的线索
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-14 border-t-2 border-foreground-950/80 pt-6">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              接着翻这几条
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
};

export default ArticleDetail;